/**
 * TaskCommentsView
 *
 * Details-page-only view of the Task Comments area. Strips the inline comment
 * input (it lives in `TaskFloatingCommentInput` at the page level) and adds
 * the new sticky / expandable description behavior. The legacy `TaskComments`
 * component is preserved for the drawer view, which keeps its original UX.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback, memo } from 'react';
import { Card, Row, Col, Typography, message, Modal, Image } from 'antd';
import { DeleteOutlined, EditOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { CopyBlock } from 'react-code-blocks';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { S3Image } from '../../../CampaignLauncher/components';
import S3File from '../../../../shared/S3File';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import { startCase } from 'lodash';
import { codeBlock } from '../../utils/task.utils';
import { getRelativeTime } from '../../../../shared/GetRelativeTime';
import TaskEditableCommentDetails from '../TaskEditableCommentDetails';
import '../../styles/index.scss';
import { space } from '../../configs/designTokens';

const { Text } = Typography;

// Module-scope pure helper — defining it inside the component recreates the
// function on every render and prevents memoization downstream.
const parseMarkdown = (text) => {
  const elements = [];
  const lines = text.split('\n');
  let currentList = [];

  const processInline = (content) => {
    const parts = [];
    const regex = /(\*\*)(.*?)\1|(\*)(.*?)\3|(~~)(.*?)\5|(`)(.*?)\7|(https?:\/\/\S+)|(.)/gs;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(content)) !== null) {
      const [
        full, // eslint-disable-line no-unused-vars
        boldOpen,
        boldContent,
        italicOpen,
        italicContent,
        strikeOpen,
        strikeContent,
        codeOpen,
        codeContent,
        url,
        other,
      ] = match;

      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }

      if (boldOpen) {
        parts.push(<strong key={`bold-${match.index}`}>{boldContent}</strong>);
      } else if (italicOpen) {
        parts.push(<em key={`italic-${match.index}`}>{italicContent}</em>);
      } else if (strikeOpen) {
        parts.push(<del key={`strike-${match.index}`}>{strikeContent}</del>);
      } else if (codeOpen) {
        parts.push(<code key={`code-${match.index}`}>{codeContent}</code>);
      } else if (url) {
        parts.push(
          <a key={`link-${match.index}`} href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        );
      } else {
        parts.push(other);
      }

      lastIndex = regex.lastIndex;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts;
  };

  lines.forEach((line, index) => {
    if (line.startsWith('- ')) {
      const content = line.substring(2);
      currentList.push(<li key={`li-${index}`}>{processInline(content)}</li>);
    } else {
      if (currentList.length > 0) {
        elements.push(<ul key={`ul-${index}`}>{currentList}</ul>);
        currentList = [];
      }
      if (line.trim()) {
        elements.push(<span key={`line-${index}`}>{processInline(line)}</span>);
      }
      if (index !== lines.length - 1) {
        elements.push(<br key={`br-${index}`} />);
      }
    }
  });

  if (currentList.length > 0) {
    elements.push(<ul key="ul-end">{currentList}</ul>);
  }

  return elements;
};

const renderParsed = (rawText) =>
  codeBlock(rawText || '').map((part, idx) =>
    part.type === 'text' ? (
      <span key={`text-${idx}`}>{parseMarkdown(part.content)}</span>
    ) : (
      <pre key={`code-${idx}`} className="task-comment__code-block">
        <code>{part.content}</code>
      </pre>
    )
  );

// Memoized comment row — re-renders only when its own data or edit state changes,
// not every time a sibling state (e.g. descriptionExpanded) flips.
const CommentRow = memo(function CommentRow({
  comment,
  fallbackAuthorName,
  isEditing,
  canManage,
  updateCommentLoading,
  onEdit,
  onDelete,
  onCancelEdit,
  refetchTask,
  updateComment,
  onTaskUpdate,
  isLast,
  bottomRef,
}) {
  const rawText = useMemo(() => {
    if (typeof comment.comment === 'object' && comment.comment !== null) {
      return comment.comment.text || 'No text available';
    }
    return comment.comment || '';
  }, [comment.comment]);

  const parsedBody = useMemo(() => renderParsed(rawText), [rawText]);

  const authorName = useMemo(() => {
    const createdBy = comment.createdBy;
    return startCase(
      createdBy && typeof createdBy === 'object'
        ? createdBy.name || createdBy.username
        : createdBy || fallbackAuthorName || 'Unknown'
    );
  }, [comment.createdBy, fallbackAuthorName]);

  const relativeTime = useMemo(() => getRelativeTime(comment.createdAt), [comment.createdAt]);

  return (
    <Card
      className="task-comment-item task-comment-row"
      ref={isLast ? bottomRef : null}
    >
      <Row className="task-comment__header">
        <AvatarS3
          imgKey={
            comment.avatar ||
            (comment.createdBy && comment.createdBy.avatar && comment.createdBy.avatar.key)
          }
          username={(comment.createdBy && comment.createdBy.name) || fallbackAuthorName}
          avatarProps={{ size: 45 }}
          style={{ marginRight: space[3] }}
        />
        <div style={{ flex: 1 }}>
          <div className="task-comment__author-row">
            <Text className="task-comment__author-name">{authorName}</Text>
            {!isEditing && canManage && (
              <Row className="task-comment-actions">
                <EditOutlined
                  className="task-icon-hover-base task-icon-hover-edit"
                  disabled={updateCommentLoading}
                  onClick={() => onEdit(comment.id)}
                />
                <DeleteOutlined
                  className="task-icon-hover-base task-icon-hover-delete"
                  onClick={() => onDelete(comment.id)}
                />
              </Row>
            )}
            <span className="task-comment__timestamp">{relativeTime}</span>
          </div>
          {isEditing ? (
            <TaskEditableCommentDetails
              comment={comment}
              isEditing={isEditing}
              setIsEditing={onCancelEdit}
              refetchTask={refetchTask}
              updateComment={updateComment}
              onTaskUpdate={onTaskUpdate}
            />
          ) : (
            <Text className="task-comment__content">{parsedBody}</Text>
          )}
        </div>
      </Row>
      {comment.attachedFiles && !isEditing && (
        <Row className="task-comment__attachments">
          {comment.attachedFiles.map((file, index) => (
            <S3File key={index} file={file} />
          ))}
        </Row>
      )}
      {comment.attachedImages && !isEditing && (
        <Image.PreviewGroup>
          <div className="task-comment__images">
            {comment.attachedImages.map((file, index) => (
              <S3Image
                key={index}
                imgKey={file.key}
                height="25%"
                width="25%"
                style={{ maxWidth: '100px' }}
              />
            ))}
          </div>
        </Image.PreviewGroup>
      )}
      {typeof comment.codeSnippet === 'string' && comment.codeSnippet.trim() && (
        <Col className="task-comment__code-snippet">
          <CopyBlock
            text={comment.codeSnippet}
            language="javascript"
            wrapLongLines
            showLineNumbers={false}
            theme="dracula"
            codeBlock
          />
        </Col>
      )}
    </Card>
  );
});

export const TaskCommentsView = (props) => {
  const { currentUser, refetchTask, updateComment, updateCommentLoading } = useTaskContext();

  const currentTask = props;
  const createdBy = startCase(
    currentTask &&
      currentTask.currentTask &&
      currentTask.currentTask.createdBy &&
      currentTask.currentTask.createdBy.name
      ? currentTask.currentTask.createdBy.name
      : currentTask &&
          currentTask.currentTask &&
          currentTask.currentTask.createdBy &&
          currentTask.currentTask.createdBy.username
  );
  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  // Task description data
  const taskDetails = currentTask?.currentTask?.taskDetails;
  const taskAttachedFiles = currentTask?.currentTask?.attachedFiles || [];
  const taskAttachedImages = currentTask?.currentTask?.attachedImages || [];
  const taskCodeSnippet = currentTask?.currentTask?.codeSnippet;
  const hasDescription =
    taskDetails?.trim() ||
    taskAttachedFiles.length > 0 ||
    taskAttachedImages.length > 0 ||
    (typeof taskCodeSnippet === 'string' && taskCodeSnippet.trim());

  const [buttonDisabled, setButtonDisabled] = useState(false); // delete-modal loading
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ visible: false, commentId: null });
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const bottomRef = useRef(null);
  const listRef = useRef(null);
  const descriptionContentRef = useRef(null);

  const displayedComments =
    (currentTask && currentTask.currentTask && currentTask.currentTask.attachedComments) || [];
  const displayedCommentsCount = displayedComments.length;

  // Detect whether the comments list actually overflows its container — used
  // to hide the card border/background when the content fits naturally.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    let frame = 0;
    const check = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = el.scrollHeight > el.clientHeight + 1;
        setIsScrollable((prev) => (prev === next ? prev : next));
      });
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [displayedCommentsCount, descriptionExpanded]);

  // Detect whether the collapsed description actually overflows. The toggle
  // button is only meaningful when there's content to reveal — short
  // descriptions skip the Show more/less affordance entirely.
  useEffect(() => {
    if (!hasDescription) {
      setDescriptionOverflows(false);
      return undefined;
    }
    const el = descriptionContentRef.current;
    if (!el) return undefined;

    let frame = 0;
    // Measure against the collapsed max-height (160px, mirrors
    // `.task-description-inline--collapsed` in _comments.scss). Measuring
    // raw scrollHeight against clientHeight only works when collapsed; when
    // expanded the cap is removed, so derive the threshold ourselves.
    const COLLAPSED_MAX_HEIGHT = 160;
    const check = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = el.scrollHeight > COLLAPSED_MAX_HEIGHT + 1;
        setDescriptionOverflows((prev) => (prev === next ? prev : next));
      });
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [hasDescription, taskDetails, taskAttachedFiles.length, taskAttachedImages.length, taskCodeSnippet]);

  const parsedDescription = useMemo(
    () => (taskDetails ? renderParsed(taskDetails) : null),
    [taskDetails]
  );

  const handleEdit = useCallback((id) => setEditingCommentId(id), []);
  const handleDelete = useCallback(
    (id) => setDeleteModal({ visible: true, commentId: id }),
    []
  );
  const handleCancelEdit = useCallback(() => setEditingCommentId(null), []);
  const toggleDescription = useCallback(() => setDescriptionExpanded((prev) => !prev), []);

  return (
    <div
      className={`task-comments-wrapper${
        isScrollable ? ' task-comments-wrapper--scrollable' : ''
      }`}
    >
      {/* Task Description — sibling above the comments card, sticky to the
          page/viewport top so it stays visible when the page scrolls. */}
      {hasDescription && (
        <div
          className={`task-description-inline task-description-inline--header${
            descriptionExpanded || !descriptionOverflows
              ? ''
              : ' task-description-inline--collapsed'
          }`}
        >
          <div className="task-description-inline__label">
            <Text strong>DESCRIPTION</Text>
          </div>
          <div className="task-description-inline__content" ref={descriptionContentRef}>
            {parsedDescription && (
              <div className="task-description-inline__text">{parsedDescription}</div>
            )}
            {taskAttachedFiles.length > 0 && (
              <Row className="task-description-inline__attachments">
                {taskAttachedFiles.map((file, idx) => (
                  <S3File key={idx} file={file} />
                ))}
              </Row>
            )}
            {taskAttachedImages.length > 0 && (
              <Image.PreviewGroup>
                <div className="task-description-inline__images">
                  {taskAttachedImages.map((file, idx) => (
                    <S3Image
                      key={idx}
                      imgKey={file.key}
                      height="25%"
                      width="25%"
                      style={{ maxWidth: '100px' }}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            )}
            {typeof taskCodeSnippet === 'string' && taskCodeSnippet.trim() && (
              <Col className="task-description-inline__code-snippet">
                <CopyBlock
                  text={taskCodeSnippet}
                  language="javascript"
                  wrapLongLines
                  showLineNumbers={false}
                  theme="dracula"
                  codeBlock
                />
              </Col>
            )}
          </div>
          {descriptionOverflows && (
            <button
              type="button"
              className="task-description-inline__toggle"
              onClick={toggleDescription}
            >
              {descriptionExpanded ? (
                <>
                  <UpOutlined /> Show less
                </>
              ) : (
                <>
                  <DownOutlined /> Show more
                </>
              )}
            </button>
          )}
        </div>
      )}
      <Card className="task-comment-card">
        <Col className="task-comment-list" ref={listRef}>
          {displayedComments.length === 0 && (
            <div className="task-comment-list__empty">
              <Text type="secondary">No comments yet. Be the first to comment!</Text>
            </div>
          )}

          {displayedComments.map((comment, index) => {
            const isCommentOwner =
              (comment.createdBy &&
                typeof comment.createdBy === 'object' &&
                comment.createdBy.id === currentUser.id) ||
              comment.createdBy === currentUser.id;
            const canManage = level === 'PLATFORM' && isCommentOwner;

            return (
              <CommentRow
                key={comment.id}
                comment={comment}
                fallbackAuthorName={createdBy}
                isEditing={editingCommentId === comment.id}
                canManage={canManage}
                updateCommentLoading={updateCommentLoading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onCancelEdit={handleCancelEdit}
                refetchTask={refetchTask}
                updateComment={updateComment}
                onTaskUpdate={props.onTaskUpdate}
                isLast={index === displayedComments.length - 1}
                bottomRef={bottomRef}
              />
            );
          })}
        </Col>
      </Card>

      {deleteModal.visible && (
        <Modal
          title="Confirm Deletion"
          centered
          visible={deleteModal.visible}
          confirmLoading={buttonDisabled}
          onOk={async () => {
            try {
              setButtonDisabled(true);
              message.loading('Deleting comment...', 0);
              await updateComment({
                variables: {
                  where: { id: deleteModal.commentId },
                  data: {
                    task: { disconnect: true },
                  },
                },
              });
              await refetchTask();

              if (props.onTaskUpdate) {
                props.onTaskUpdate();
              }

              message.destroy();
              message.success('Comment deleted successfully');
            } catch (error) {
              message.error(`Failed to delete comment: ${error.message}`);
            } finally {
              setDeleteModal({ visible: false, commentId: null });
              setButtonDisabled(false);
            }
          }}
          onCancel={() => setDeleteModal({ visible: false, commentId: null })}
        >
          <p>Are you sure you want to delete this comment?</p>
        </Modal>
      )}
    </div>
  );
};
